import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestCurrentAccountDeletion } from "@/features/profile/api/profileClient";
import { useAuthStore } from "@/stores/authStore";

export function AccountDeletionForm() {
  const { t } = useTranslation();
  const logOut = useAuthStore((state) => state.logOut);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    if (!password) {
      setSubmitError(t("auth.validation.required"));
      return;
    }

    setBusy(true);
    try {
      await requestCurrentAccountDeletion(password);
      await logOut();
    } catch {
      setSubmitError(t("profile.deleteAccount.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-4 rounded-[1.5rem] border border-clay/20 bg-clay/5 p-4" onSubmit={handleSubmit}>
      <div>
        <h3 className="text-base font-semibold text-ink">{t("profile.deleteAccount.title")}</h3>
        <p className="mt-2 text-sm leading-6 text-ink/65">
          {t("profile.deleteAccount.description")}
        </p>
      </div>
      <div>
        <Label htmlFor="delete-account-password">{t("auth.fields.currentPassword")}</Label>
        <Input
          id="delete-account-password"
          autoComplete="current-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>
      {submitError ? <FormMessage>{submitError}</FormMessage> : null}
      <Button disabled={busy} type="submit" variant="destructive">
        {busy ? t("common.saving") : t("profile.deleteAccount.submit")}
      </Button>
    </form>
  );
}
